import { useEffect, useRef, useState } from "react";
import { LiquidMetal } from "@paper-design/shaders-react";

// User-provided custom shape (leaf-like blob) rendered as a data URI so the
// LiquidMetal shader can consume it directly as an image input.
const shapeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="659" height="712" viewBox="0 0 659 712" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.78271 117.67C0.934761 25.1238 102.93 -31.0363 181.216 18.3206L621.963 296.197C670.691 326.918 669.998 398.196 620.683 427.964L178.487 694.885C99.9853 742.271 -0.150499 685.642 0.000218178 593.948L0.0206045 581.551C1.14094 527.857 16.7406 474.452 39.193 430.62C63.8447 382.494 64.458 303.997 39.0403 256.272C19.4021 219.398 4.92023 177.854 0.743402 141.638L0.78271 117.67Z" fill="white"/></svg>`;

const shapeDataUri = `data:image/svg+xml;utf8,${encodeURIComponent(shapeSvg)}`;

// SVG turbulence-based displacement filter for real glass refraction warp
const glassFilterSvg = `<svg xmlns="http://www.w3.org/2000/svg"><filter id="glassWarp"><feTurbulence type="fractalNoise" baseFrequency="0.012 0.018" numOctaves="2" seed="7"/><feDisplacementMap in="SourceGraphic" scale="26"/></filter></svg>`;
const glassFilterUri = `data:image/svg+xml;utf8,${encodeURIComponent(glassFilterSvg)}#glassWarp`;

const SIZE = 527; // 15% smaller than the previous 620

export default function LiquidMetalBackground() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const squashRef = useRef<HTMLDivElement | null>(null);
  const posRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const anchorRef = useRef({ x: 0, y: 0 });
  // Base anchor (before scroll parallax) — the resting spot on a non-scrolled page
  const baseAnchorRef = useRef({ x: 0, y: 0 });
  // How strongly the anchor drifts against scroll — higher = more parallax travel
  const PARALLAX = 0.18;
  const velRef = useRef({ x: 0, y: 0 });
  const stretchRef = useRef(0);
  const angleRef = useRef(0);
  const modeRef = useRef<"idle" | "drag" | "spring">("idle");
  const rafRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  // Opacity fades as the user scrolls past the hero — keeps focus on downstream content.
  const [opacity, setOpacity] = useState(1);

  // Set initial position on the right side and start the RAF animation loop
  useEffect(() => {
    const computeBaseAnchor = () => ({
      x: Math.max(window.innerWidth - SIZE - 40, window.innerWidth / 2),
      y: Math.max(window.innerHeight / 2 - SIZE / 2, 40),
    });
    const applyScrollAnchor = () => {
      const base = baseAnchorRef.current;
      anchorRef.current = {
        x: base.x,
        // Parallax against scroll: element drifts opposite to scroll direction,
        // clamped so it never sails wildly off-screen on long pages.
        y: base.y - Math.max(Math.min(window.scrollY * PARALLAX, 220), -220),
      };
    };

    baseAnchorRef.current = computeBaseAnchor();
    applyScrollAnchor();
    const initX = anchorRef.current.x;
    const initY = anchorRef.current.y;
    posRef.current = { x: initX, y: initY };
    targetRef.current = { x: initX, y: initY };
    if (containerRef.current) {
      containerRef.current.style.transform = `translate3d(${initX}px, ${initY}px, 0)`;
    }

    // Keep the anchor sensibly placed if the window is resized
    const handleResize = () => {
      baseAnchorRef.current = computeBaseAnchor();
      applyScrollAnchor();
      if (modeRef.current !== "drag") {
        targetRef.current = { ...anchorRef.current };
      }
    };
    window.addEventListener("resize", handleResize);

    // Fade out once the user scrolls past the hero section so the glass ball
    // doesn't fight for attention with the content below.
    const computeOpacity = () => {
      const vh = window.innerHeight;
      // Start fading at 55% of the viewport, fully gone by 95%.
      const start = vh * 0.55;
      const end = vh * 0.95;
      const y = window.scrollY;
      if (y <= start) return 1;
      if (y >= end) return 0;
      return 1 - (y - start) / (end - start);
    };

    // Scroll parallax — updates the anchor's Y position so idle & spring modes
    // both pull the object gently up/down as the user scrolls the page.
    const handleScroll = () => {
      applyScrollAnchor();
      if (modeRef.current === "idle") {
        targetRef.current = { ...anchorRef.current };
      }
      setOpacity(computeOpacity());
    };
    // Initial pass so a page loaded mid-scroll (e.g. via back/forward) is correct.
    setOpacity(computeOpacity());
    window.addEventListener("scroll", handleScroll, { passive: true });

    const STIFFNESS = 0.08; // spring pull strength toward the anchor
    const DAMPING = 0.82;   // < 1 = friction; lower = snappier, higher = bouncier

    const loop = () => {
      const p = posRef.current;

      if (modeRef.current === "spring") {
        // Elastic spring back to the anchor (Hooke's law + damping → natural bounce)
        const a = anchorRef.current;
        const v = velRef.current;
        const ax = (a.x - p.x) * STIFFNESS;
        const ay = (a.y - p.y) * STIFFNESS;
        v.x = (v.x + ax) * DAMPING;
        v.y = (v.y + ay) * DAMPING;
        p.x += v.x;
        p.y += v.y;

        // Rest condition — once movement is tiny, settle exactly on the anchor
        if (
          Math.abs(v.x) < 0.05 &&
          Math.abs(v.y) < 0.05 &&
          Math.abs(a.x - p.x) < 0.3 &&
          Math.abs(a.y - p.y) < 0.3
        ) {
          p.x = a.x;
          p.y = a.y;
          v.x = 0;
          v.y = 0;
          // Sync target to anchor so the idle follow loop doesn't drag the
          // object back toward the stale drag position after settling.
          targetRef.current = { x: a.x, y: a.y };
          modeRef.current = "idle";
        }
      } else {
        // Idle & drag both use a smooth follow toward the target
        const t = targetRef.current;
        p.x += (t.x - p.x) * 0.22;
        p.y += (t.y - p.y) * 0.22;
      }

      // ── Squash & Stretch ──────────────────────────────────────────
      // Stretch along velocity direction, squish perpendicular → cartoon rubber feel.
      let targetStretch = 0;
      if (modeRef.current === "spring") {
        const speed = Math.hypot(velRef.current.x, velRef.current.y);
        // cap so it never gets grotesque, tuned for a punchy but tasteful pop
        targetStretch = Math.min(speed * 0.014, 0.24);
        // Only update the stretch angle when there's meaningful movement,
        // otherwise the tiny end-of-bounce jitter would spin the axis wildly.
        if (speed > 0.6) {
          angleRef.current =
            (Math.atan2(velRef.current.y, velRef.current.x) * 180) / Math.PI;
        }
      } else if (modeRef.current === "drag") {
        // Gentle compression while being held — like getting pinched
        targetStretch = -0.05;
      }
      // Smooth easing toward the target stretch so transitions feel gooey
      stretchRef.current += (targetStretch - stretchRef.current) * 0.2;

      if (containerRef.current) {
        containerRef.current.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
      }
      if (squashRef.current) {
        const s = stretchRef.current;
        const ang = angleRef.current;
        // Rotate to velocity direction → scale non-uniformly → rotate back.
        // Result: stretched along direction of motion, squished across it.
        squashRef.current.style.transform = `rotate(${ang}deg) scale(${1 + s}, ${1 - s}) rotate(${-ang}deg)`;
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    draggingRef.current = true;
    modeRef.current = "drag";
    velRef.current = { x: 0, y: 0 };
    setIsDragging(true);
    containerRef.current.setPointerCapture(e.pointerId);
    dragOffsetRef.current = {
      x: e.clientX - posRef.current.x,
      y: e.clientY - posRef.current.y,
    };
    // Immediately target the current position so nothing jumps
    targetRef.current = { x: posRef.current.x, y: posRef.current.y };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    targetRef.current = {
      x: e.clientX - dragOffsetRef.current.x,
      y: e.clientY - dragOffsetRef.current.y,
    };
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    if (containerRef.current) {
      try {
        containerRef.current.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    // Kick off elastic spring-back to the anchor position. Also point the
    // idle target at the anchor now, so if spring exits to idle later, the
    // idle loop won't yank the object toward the stale drag position.
    targetRef.current = { x: anchorRef.current.x, y: anchorRef.current.y };
    velRef.current = { x: 0, y: 0 };
    modeRef.current = "spring";
  };

  // Mask style clips a layer to the exact blob silhouette
  const maskStyle: React.CSSProperties = {
    WebkitMaskImage: `url("${shapeDataUri}")`,
    maskImage: `url("${shapeDataUri}")`,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="fixed left-0 top-0 z-30 select-none touch-none"
      style={{
        width: SIZE,
        height: SIZE,
        cursor: isDragging ? "grabbing" : "grab",
        willChange: "transform, opacity",
        perspective: "1400px",
        opacity,
        // Smooth the opacity change so any tiny scroll snaps don't flicker.
        transition: "opacity 0.25s ease-out",
        // Ignore clicks when fully faded so the content below stays fully interactive.
        pointerEvents: opacity < 0.05 ? "none" : "auto",
      }}
    >
      {/* Local keyframes for breathing + slow 3D tilt/rotation */}
      <style>{`
        @keyframes lm-breathe {
          0%, 100% { transform: rotateX(6deg) rotateY(-8deg) rotateZ(-2deg) scale(0.97); }
          50%      { transform: rotateX(-4deg) rotateY(9deg) rotateZ(2deg) scale(1.03); }
        }
        @keyframes lm-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes lm-shadow-breathe {
          0%, 100% { opacity: 0.55; transform: translateY(24px) scale(0.9); filter: blur(28px); }
          50%      { opacity: 0.75; transform: translateY(18px) scale(0.95); filter: blur(34px); }
        }
      `}</style>

      {/* Ground shadow beneath the object — sells the "floating in space" 3D feel */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          ...maskStyle,
          background: "rgba(0,0,0,0.55)",
          animation: "lm-shadow-breathe 7s ease-in-out infinite",
          transformOrigin: "center center",
        }}
      />

      {/* Squash & stretch wrapper — deformed by velocity so the glass feels rubbery */}
      <div
        ref={squashRef}
        className="absolute inset-0 pointer-events-none"
        style={{
          transformOrigin: "50% 50%",
          willChange: "transform",
        }}
      >
        {/* 3D breathing stage — tilts and pulses the whole glass object */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transformStyle: "preserve-3d",
            animation: "lm-breathe 9s ease-in-out infinite",
            transformOrigin: "center center",
          }}
        >
          {/* Layer 0 — back rim / thickness: a slightly larger, darker copy pushed back in Z */}
          <div
            className="absolute inset-0"
            style={{
              ...maskStyle,
              background:
                "radial-gradient(80% 80% at 60% 70%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 100%)",
              transform: "translateZ(-40px) scale(1.02)",
            }}
          />

          {/* Layer 1 — Glass refraction: warps + blurs the text behind, clipped to the blob shape */}
          <div
            className="absolute inset-0"
            style={{
              ...maskStyle,
              backdropFilter: "blur(6px) saturate(1.35) contrast(1.08)",
              WebkitBackdropFilter: "blur(6px) saturate(1.35) contrast(1.08)",
              filter: `url("${glassFilterUri}")`,
            }}
          />

          {/* Layer 2 — Inner volume gradient (dome lighting) for glass depth */}
          <div
            className="absolute inset-0"
            style={{
              ...maskStyle,
              background:
                "radial-gradient(120% 120% at 30% 22%, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0) 55%), radial-gradient(120% 120% at 75% 85%, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.05) 45%, rgba(0,0,0,0) 75%)",
            }}
          />

          {/* Layer 3 — Slow-rotating liquid metal shader wrapper for a subtle spin */}
          <div
            className="absolute inset-0"
            style={{
              animation: "lm-spin 32s linear infinite",
              transformOrigin: "center center",
              transform: "translateZ(20px)",
            }}
          >
            <LiquidMetal
              fit="contain"
              scale={0.9}
              rotation={0}
              offsetX={0}
              offsetY={0}
              speed={0.7}
              originX={0.5}
              originY={0.5}
              worldWidth={0}
              worldHeight={0}
              colorBack="#00000000"
              colorTint="#ffffff"
              distortion={0.12}
              repetition={1}
              shiftRed={0.12}
              shiftBlue={0.18}
              contour={1}
              softness={0.4}
              angle={331}
              image={shapeDataUri}
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                mixBlendMode: "screen",
                opacity: 0.8,
              }}
            />
          </div>

          {/* Layer 4 — Front rim highlight: bright arc across the top edge, sitting closest in Z */}
          <div
            className="absolute inset-0"
            style={{
              ...maskStyle,
              background:
                "radial-gradient(60% 40% at 40% 12%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0) 70%)",
              mixBlendMode: "screen",
              transform: "translateZ(35px)",
            }}
          />
        </div>
      </div>
    </div>
  );
}